import { ActionIcon, Alert, Blockquote, Button, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IComment, IEditorMetadata, IUserDepricated } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconMessageCircle, IconPencil, IconSend, IconTrash, IconX } from "@tabler/icons-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog } from "..";
import { formatDateDmyDotsWithTime } from "../../app/routes/admin/selectors";
import ErrorBoundary from "../../util/ErrorBoundary";
import Avatar from "../Avatar";
import RichTextEditor, {
  lexicalStateToPlainText,
  plainTextToLexicalState,
  RichEditorHandle,
  RichTextReader,
} from "../RichTextEditorMantine/RichTextEditor";
import EmojiReactions from "./EmojiReactions";
import classes from "./SingleComment.module.css";

export const CiteComponent: React.FC<{ name: string; date: Date; actions?: React.ReactNode }> = ({
  name,
  date,
  actions,
}) => {
  const formatted = useMemo(() => formatDateDmyDotsWithTime(date), [date]);

  return (
    <Group justify="space-between" align="center" wrap="wrap" gap="xs" mt="xs">
      <Text size="sm" fs="italic" c="dimmed" style={{ opacity: 0.6 }}>
        <Text span fw={700}>
          {name}
        </Text>{" "}
        - {formatted}
      </Text>
      {actions && (
        <Group gap={0} align="center" wrap="wrap">
          {actions}
        </Group>
      )}
    </Group>
  );
};

const SingleComment: React.FC<{
  comment: IComment;
  editEnabled: boolean;
  deleteEnabled: boolean;
  members: IProjectMemberApiItem[];
  onSave: (text: string, metadata: IEditorMetadata) => void;
  onDelete: () => void;
  reactions?: { user?: { _id?: string; name?: string }; date?: string | Date; emoji?: string }[];
  onReact?: (emoji: string) => void;
  currentUserId?: string;
  onReply?: (text: string, metadata: IEditorMetadata) => void | Promise<void>;
  onSaveReply?: (replyId: string, text: string, metadata: IEditorMetadata) => void | Promise<void>;
  onDeleteReply?: (replyId: string) => void | Promise<void>;
  onReactReply?: (replyId: string, emoji: string) => void | Promise<void>;
}> = ({
  comment,
  editEnabled,
  deleteEnabled,
  onSave,
  onDelete,
  members,
  reactions,
  onReact,
  currentUserId,
  onReply,
  onSaveReply,
  onDeleteReply,
  onReactReply,
}) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [replyMode, setReplyMode] = useState<boolean>(false);
  const hasReactionControls = Boolean(onReact && currentUserId);
  const hasReplyControls = Boolean(onReply);
  const hasFooterControls = editEnabled || deleteEnabled || hasReactionControls || hasReplyControls;
  const replies = (comment.replies ?? []).filter((reply) => reply.deleted !== true);

  const getCreatedById = (comment: IComment) =>
    typeof comment.createdBy === "string" ? comment.createdBy : comment.createdBy?._id;

  const createdById = getCreatedById(comment);

  const createdByName = comment.createdByName ?? (comment.createdBy as IUserDepricated)?.name ?? "Ukjent";

  const createdDate = useMemo(() => {
    const raw = comment.createdDate || comment.createdAt;
    return raw ? new Date(raw) : new Date();
  }, [comment.createdDate, comment.createdAt]);

  const avatarIcon = <Avatar id={createdById} name={createdByName} />;
  const commentActions = hasFooterControls ? (
    <>
      {hasReactionControls && (
        <EmojiReactions reactions={reactions ?? []} currentUserId={currentUserId!} onReact={onReact!} />
      )}

      {hasReplyControls && !replyMode && (
        <Tooltip label="Svar" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            className={classes.actionButton}
            onClick={() => setReplyMode(true)}
          >
            <IconMessageCircle size={16} />
          </ActionIcon>
        </Tooltip>
      )}

      {deleteEnabled && (
        <ConfirmDialog
          confirmCb={() => onDelete()}
          title="Slett kommentar"
          message="Er du sikker på at du vil slette denne kommentar?"
        >
          <Tooltip label="Slett" withArrow>
            <ActionIcon variant="subtle" color="gray" size="sm" className={classes.actionButton}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </ConfirmDialog>
      )}

      {editEnabled && !editMode && (
        <Tooltip label="Rediger" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            className={classes.actionButton}
            onClick={() => setEditMode(true)}
          >
            <IconPencil size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </>
  ) : null;

  return (
    <ErrorBoundary customComponent={<Alert color="danger">En feil skjedde! Melding kan ikke vises.</Alert>}>
      <>
        <Blockquote
          className={classes.comment}
          icon={avatarIcon}
          mt="xs"
          styles={{
            root: {
              paddingTop: "1rem",
              paddingBottom: "0.5rem",
              paddingRight: "0.5rem",
              position: "relative",
            },
          }}
        >
          {editMode === false && <RichTextReader richTextTree={comment.text} />}

          <EditComment
            editMode={editMode}
            setEditMode={setEditMode}
            text={comment.text}
            onSave={onSave}
            members={members}
          />

          <CiteComponent name={createdByName} date={createdDate} actions={commentActions} />

          {replyMode && onReply && (
            <ReplyComment
              members={members}
              onReply={onReply}
              onCancel={() => {
                setReplyMode(false);
              }}
              onSaved={() => {
                setReplyMode(false);
              }}
            />
          )}
        </Blockquote>

        {replies.length > 0 && (
          <Stack gap="xs" className={classes.replies}>
            {replies.map((reply) => (
              <SingleComment
                key={reply._id}
                comment={reply}
                editEnabled={String(currentUserId ?? "") === String(getCreatedById(reply) ?? "")}
                deleteEnabled={String(currentUserId ?? "") === String(getCreatedById(reply) ?? "")}
                onSave={(text, metadata) => {
                  if (reply._id) onSaveReply?.(reply._id, text, metadata);
                }}
                onDelete={() => {
                  if (reply._id) onDeleteReply?.(reply._id);
                }}
                members={members}
                reactions={reply.reactions}
                onReact={
                  onReactReply
                    ? (emoji) => {
                        if (reply._id) onReactReply(reply._id, emoji);
                      }
                    : undefined
                }
                currentUserId={currentUserId}
              />
            ))}
          </Stack>
        )}
      </>
    </ErrorBoundary>
  );
};

export default SingleComment;

const EditComment: React.FC<{
  editMode: boolean;
  setEditMode: (bool: boolean) => void;
  text: string;
  onSave: (text: string, metadata: IEditorMetadata) => void;
  members: IProjectMemberApiItem[];
}> = ({ editMode, setEditMode, text, onSave, members }) => {
  const [editedText, setEditedText] = useState<any>(null);
  const [editorMetadata, setEditorMetadata] = useState<IEditorMetadata>(null);
  const [hasImages, setHasImages] = useState(false);

  const abortUpdate = () => {
    setEditedText(null);
    setEditMode(false);
    setEditorMetadata(null);
  };

  const save = async () => {
    const plainText = await lexicalStateToPlainText(editedText);
    onSave(JSON.stringify(editedText), { ...editorMetadata, plainText });
    setEditMode(false);
    setEditorMetadata(null);
  };

  useEffect(() => {
    const formatText = async () => {
      const result = await plainTextToLexicalState(text);
      setEditedText(result);
    };

    if (editedText === null) {
      formatText();
    }

    const t = setTimeout(() => {
      if (editedText === null) {
        formatText();
      }
    }, 100);

    return () => clearTimeout(t);
  }, [editedText, text]);

  const handleChange = (editorState: any, _plainText: string, nextHasImages: boolean) => {
    setEditedText(editorState);
    setHasImages(nextHasImages);
  };

  if (!editMode) return null;

  return (
    <div style={{ width: "100%" }}>
      <RichTextEditor
        richTextTree={editedText}
        handleMentions={(e) => setEditorMetadata({ ...editorMetadata, mentionIdArray: e })}
        onChange={handleChange}
        members={members}
      />

      <Group mt="xs" gap="xs" justify="flex-end">
        <Button
          disabled={(!editedText || editedText.length < 1) && hasImages === false}
          leftSection={<IconSend size={16} />}
          variant="filled"
          size="sm"
          onClick={async () => {
            await save();
          }}
        >
          Lagre
        </Button>

        <Button onClick={() => abortUpdate()} size="sm" variant="default">
          <IconX size={16} />
        </Button>
      </Group>
    </div>
  );
};

const ReplyComment: React.FC<{
  members: IProjectMemberApiItem[];
  onReply: (text: string, metadata: IEditorMetadata) => void | Promise<void>;
  onCancel: () => void;
  onSaved: () => void;
}> = ({ members, onReply, onCancel, onSaved }) => {
  const [editorState, setEditorState] = useState<any>(null);
  const [editorMetadata, setEditorMetadata] = useState<IEditorMetadata>(null);
  const [plainText, setPlainText] = useState("");
  const [hasImages, setHasImages] = useState(false);
  const editorRef = useRef<RichEditorHandle | null>(null);

  const save = async () => {
    const nextPlainText = await lexicalStateToPlainText(editorState);
    await onReply(JSON.stringify(editorState), { ...editorMetadata, plainText: nextPlainText });
    setEditorState(null);
    setEditorMetadata(null);
    setPlainText("");
    setHasImages(false);
    editorRef.current?.reset();
    onSaved();
  };

  return (
    <Stack gap="xs" mt="sm">
      <RichTextEditor
        ref={editorRef}
        members={members}
        onChange={(nextEditorState, nextPlainText, nextHasImages) => {
          setEditorState(nextEditorState);
          setPlainText(nextPlainText);
          setHasImages(nextHasImages);
        }}
        handleMentions={(mentionIdArray) => setEditorMetadata({ ...editorMetadata, mentionIdArray })}
      />

      <Group gap="xs" justify="flex-end">
        <Button
          disabled={(plainText.length < 1 && hasImages === false) || editorState == null}
          leftSection={<IconSend size={16} />}
          variant="filled"
          size="sm"
          onClick={async () => {
            await save();
          }}
        >
          Send
        </Button>

        <Button
          onClick={() => {
            editorRef.current?.reset();
            onCancel();
          }}
          size="sm"
          variant="default"
        >
          <IconX size={16} />
        </Button>
      </Group>
    </Stack>
  );
};
